/*
  Reference link: https://shopify.dev/docs/themes/navigation-search/search/predictive-search
*/
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]"
    );
    this.allPredictiveSearchInstances =
      document.querySelectorAll("predictive-search");
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
    this.select = this.querySelector("select");
    if (this.select) {
      this.select.addEventListener(
        "input",
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  getSearchFilterType() {
    if (!this.select) {
      return "";
    }

    return this.select.dataset.type;
  }

  getSearchFilterTypeQuery() {
    if (!this.select) {
      return "";
    }

    return this.select.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = "";
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (previousTerm && currentButtonText.split(previousTerm).length > 2) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  
  async getSearchResults(searchTerm) {
    const filterType = this.getSearchFilterType();
    const filterValue = this.getSearchFilterTypeQuery();

    const currentRoot = (
      window.Shopify?.routes?.root || '/'
    ).replace(/\/?$/, '/');

    const locale = (
      document.documentElement.lang || 'he'
    ).split('-')[0].toLowerCase();

    const cacheKey =
      `${locale}:${currentRoot}:${filterType}:${filterValue}:${searchTerm.toLowerCase()}`;

    this.setLiveRegionLoadingState();

    if (this.cachedResults[cacheKey]) {
      this.renderSearchResults(this.cachedResults[cacheKey]);
      return;
    }

    const query = filterValue
      ? `${filterType}:${filterValue}* ${searchTerm}`
      : searchTerm;

    const params = new URLSearchParams({
      q: query,
      'resources[type]': this.dataset.types || 'product',
      'resources[limit]': '10',
      section_id: 'predictive-search'
    });

    const unavailable = this.dataset.unavailable;

    if (['show', 'hide', 'last'].includes(unavailable)) {
      params.set(
        'resources[options][unavailable_products]',
        unavailable
      );
    }

    // Hebrew and Arabic use the supported English
    // predictive-search endpoint.
    // English and Russian use their locale-aware routes.
    const roots = ['he', 'ar'].includes(locale)
      ? ['/en/']
      : [...new Set([currentRoot, '/en/'])];

    for (const root of roots) {
      try {
        const url =
          `${root}search/suggest?${params.toString()}`;

        const response = await fetch(url, {
          signal: this.abortController.signal
        });

        if (!response.ok) {
          throw new Error(
            `Search request failed: ${response.status} at ${root}`
          );
        }

        const html = await response.text();

        const doc = new DOMParser().parseFromString(
          html,
          'text/html'
        );

        const section = doc.querySelector(
          '#shopify-section-predictive-search'
        );

        if (
          !section ||
          !section.querySelector('#predictive-search-results')
        ) {
          throw new Error(
            `Missing predictive-search results markup at ${root}`
          );
        }

        const markup = section.innerHTML;

        this.allPredictiveSearchInstances.forEach(instance => {
          instance.cachedResults[cacheKey] = markup;
        });

        // Avoid showing outdated suggestions.
        if (this.getQuery() !== searchTerm) return;

        this.renderSearchResults(markup);
        return;

      } catch (error) {
        if (error.name === 'AbortError') return;

        console.warn('[Nichies search]', error);
      }
    }

    this.close();
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  
renderSearchResults(resultsMarkup) {
  this.predictiveSearchResults.innerHTML = resultsMarkup;

  // Detect the language from the actual storefront URL.
  const currentPath = window.location.pathname;

  const pathLocale = currentPath.match(
    /^\/(en|ru|ar|he)(?:\/|$)/
  )?.[1];

  const language = pathLocale || 'he';

  const route = {
    he: '',
    en: '/en',
    ru: '/ru',
    ar: '/ar'
  }[language] || '';

  const translations = {
    he: {
      products: 'מוצרים',
      suggestions: 'הצעות',
      search: 'חפש',
      from: 'החל מ־'
    },
    en: {
      products: 'Products',
      suggestions: 'Suggestions',
      search: 'Search for',
      from: 'From'
    },
    ru: {
      products: 'Товары',
      suggestions: 'Предложения',
      search: 'Искать',
      from: 'от'
    },
    ar: {
      products: 'المنتجات',
      suggestions: 'اقتراحات',
      search: 'ابحث عن',
      from: 'ابتداءً من'
    }
  };

  const t = translations[language] || translations.he;
  const results = this.predictiveSearchResults;

  // 1. Translate search headings.
  const productsHeading = results.querySelector(
    '#predictive-search-products'
  );

  const suggestionsHeading = results.querySelector(
    '#predictive-search-queries'
  );

  const searchFor = results.querySelector(
    '[data-predictive-search-search-for-text]'
  );

  if (productsHeading) {
    productsHeading.textContent = t.products;
  }

  if (suggestionsHeading) {
    suggestionsHeading.textContent = t.suggestions;
  }

  if (searchFor) {
    searchFor.textContent =
      `${t.search} "${this.getQuery()}"`;
  }

  // 2. Convert all search-result links to the
  // visitor's selected storefront language.
  results.querySelectorAll('a[href]').forEach(link => {
    try {
      const url = new URL(
        link.getAttribute('href'),
        window.location.origin
      );

      if (url.origin !== window.location.origin) {
        return;
      }

      // Remove any language prefix returned by Shopify.
      const path = url.pathname.replace(
        /^\/(?:en|ru|ar|he)(?=\/|$)/,
        ''
      ) || '/';

      // Only rewrite actual storefront search links.
      if (!/^\/(products|collections|pages|blogs|search)(?:\/|$)/.test(path)) {
        return;
      }

      const correctedUrl =
        route + path + url.search + url.hash;

      link.setAttribute('href', correctedUrl);

    } catch (error) {
      // Ignore invalid URLs.
    }
  });

  // 3. Fix the "From" price label and text direction.
  results.querySelectorAll(
    '#predictive-search-results-products-list li'
  ).forEach(item => {

    const price = item.querySelector('.price');

    if (!price) return;

    const priceText = price.textContent || '';

    const displayedPrice =
      price.querySelector(
        '.price__regular .price-item--regular'
      )?.textContent || priceText;

    const match =
      displayedPrice.match(
        /₪\s*[\d.,]+|[\d.,]+\s*₪/
      ) ||
      priceText.match(
        /₪\s*[\d.,]+|[\d.,]+\s*₪/
      );

    if (!match) return;

    const amount = match[0].replace(/\s+/g, '');

    const hasFrom =
      /\bfrom\b|\bот\b|החל|ابتداء/i.test(priceText);

    const line = document.createElement('span');

    line.className = 'nichies-predictive-price-line';

    line.dir = ['he', 'ar'].includes(language)
      ? 'rtl'
      : 'ltr';

    if (hasFrom) {
      const prefix = document.createElement('span');
      prefix.textContent = t.from;

      line.append(prefix, ' ');
    }

    const money = document.createElement('bdi');
    money.dir = 'ltr';
    money.textContent = amount;

    line.append(money);

    price.replaceChildren(line);
  });

  // 4. Preserve original Shopify search behavior.
  this.setAttribute('results', true);

  this.setLiveRegionResults();
  this.open();
}


  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        .textContent
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector(".header-default").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight =
      this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("loading");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute("style");
  }
}

customElements.define("predictive-search", PredictiveSearch);
